import { useState, useCallback, useMemo } from 'react';
import { useDashboard } from '../../../context/dashboardContext';
import { computeGrandTotal } from '../../../utils/quoteTotals';

const formatCurrency = (n) => `$${(Math.round(n * 100) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const BackfillGrandTotals = () => {
  const { getQuotes, getQuoteById, updateOrCreateQuote, getAllCustomers } = useDashboard();
  const [running, setRunning] = useState(false);
  const [dryRun, setDryRun] = useState(true);
  const [log, setLog] = useState([]);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const appendLog = useCallback((entry) => {
    setLog((prev) => [...prev, { ...entry, ts: new Date().toISOString() }]);
  }, []);

  const run = useCallback(async () => {
    setRunning(true);
    setLog([]);
    setProgress({ done: 0, total: 0 });

    try {
      const customersRes = await getAllCustomers();
      if (!customersRes.success) {
        appendLog({ level: 'error', msg: `Failed to load customers: ${customersRes.error}` });
        setRunning(false);
        return;
      }
      const customerDiscountByName = new Map();
      (customersRes.data || []).forEach((c) => {
        const name = c?.customer || c?.customerData?.customer;
        if (name) customerDiscountByName.set(name, parseFloat(c.standard_discount) || 0);
      });
      appendLog({ level: 'info', msg: `Loaded ${customerDiscountByName.size} customers (for standard_discount lookup).` });

      const listRes = await getQuotes({ page: 1, limit: 1000, sort: 'desc' });
      if (!listRes.success) {
        appendLog({ level: 'error', msg: `Failed to list quotes: ${listRes.error}` });
        setRunning(false);
        return;
      }
      const quotes = listRes.data?.results || [];
      setProgress({ done: 0, total: quotes.length });
      appendLog({ level: 'info', msg: `Loaded ${quotes.length} quotes. ${dryRun ? 'DRY RUN — nothing will be written.' : 'LIVE — PATCHing each quote.'}` });

      for (let i = 0; i < quotes.length; i += 1) {
        const stub = quotes[i];
        const quoteNumber = stub.quote_number;
        if (!quoteNumber) {
          appendLog({ level: 'skip', quote: '(missing quote_number)', msg: 'no quote_number on list item' });
          setProgress((p) => ({ ...p, done: p.done + 1 }));
          continue;
        }

        const fullRes = await getQuoteById(stub._id);
        if (!fullRes.success) {
          appendLog({ level: 'error', quote: quoteNumber, msg: `getQuoteById failed: ${fullRes.error}` });
          setProgress((p) => ({ ...p, done: p.done + 1 }));
          continue;
        }
        const doc = fullRes.data;
        const parts = Array.isArray(doc?.parts) ? doc.parts : [];
        const installation = Array.isArray(doc?.installation) ? doc.installation[0] : null;
        const installationTotal = installation?.Total_Price || 0;
        const taxStr = installation?.Sales_Tax || '';

        // Prefer the discount saved on the quote (new save flow). Fall back to
        // the customer's current standard_discount for historical docs.
        const customerName = typeof doc?.customer === 'string'
          ? doc.customer
          : (doc?.customer?.customer || '');
        const savedDiscount = parseFloat(doc?.standard_discount);
        const customerDiscount = customerDiscountByName.get(customerName);
        const defaultDiscount = Number.isFinite(savedDiscount) && savedDiscount > 0
          ? savedDiscount
          : (customerDiscount || 0);

        const grandTotal = computeGrandTotal({
          parts,
          installationTotal,
          taxStr,
          defaultDiscount,
        });

        const previous = doc?.grand_total ?? null;
        if (parts.length === 0 && !installationTotal) {
          appendLog({ level: 'skip', quote: quoteNumber, msg: `no parts/installation — leaving as ${previous ?? 'unset'}`, computed: grandTotal });
          setProgress((p) => ({ ...p, done: p.done + 1 }));
          continue;
        }

        if (dryRun) {
          appendLog({ level: 'dry', quote: quoteNumber, msg: `would set grand_total = ${formatCurrency(grandTotal)} (was ${previous ?? 'unset'}) — discount used: ${defaultDiscount}%`, computed: grandTotal });
          setProgress((p) => ({ ...p, done: p.done + 1 }));
          continue;
        }

        const patchRes = await updateOrCreateQuote(quoteNumber, {
          grand_total: grandTotal,
          standard_discount: defaultDiscount,
        });
        if (patchRes.success) {
          appendLog({ level: 'ok', quote: quoteNumber, msg: `set grand_total = ${formatCurrency(grandTotal)} (was ${previous ?? 'unset'})`, computed: grandTotal });
        } else {
          appendLog({ level: 'error', quote: quoteNumber, msg: `PATCH failed: ${patchRes.error}` });
        }
        setProgress((p) => ({ ...p, done: p.done + 1 }));
      }

      appendLog({ level: 'info', msg: 'Done.' });
    } catch (err) {
      appendLog({ level: 'error', msg: `Unhandled: ${err.message}` });
    } finally {
      setRunning(false);
    }
  }, [getQuotes, getQuoteById, updateOrCreateQuote, dryRun, appendLog]);

  const counts = useMemo(() => {
    const c = { ok: 0, dry: 0, skip: 0, error: 0, info: 0 };
    log.forEach((e) => { c[e.level] = (c[e.level] || 0) + 1; });
    return c;
  }, [log]);

  return (
    <div className="p-6 max-w-5xl">
      <h1 className="text-2xl font-bold text-red-700 uppercase tracking-wide mb-2">Backfill Grand Totals</h1>
      <p className="text-sm text-gray-600 mb-4">
        One-shot migration: recomputes <code>grand_total</code> locally for every quote and PATCHes it via <code>/quotes/update/:n</code> (no new versions created). Run dry-run first to see what would change.
      </p>

      <div className="flex items-center gap-4 mb-4">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} disabled={running} />
          Dry run (preview only)
        </label>
        <button
          onClick={run}
          disabled={running}
          className="px-4 py-2 bg-red-700 text-white rounded disabled:opacity-50"
        >
          {running ? 'Running...' : dryRun ? 'Preview backfill' : 'Run backfill (LIVE)'}
        </button>
        {progress.total > 0 && (
          <span className="text-sm text-gray-700">
            {progress.done} / {progress.total}
          </span>
        )}
      </div>

      <div className="text-sm mb-3">
        <span className="mr-3"><strong>ok:</strong> {counts.ok}</span>
        <span className="mr-3"><strong>dry:</strong> {counts.dry}</span>
        <span className="mr-3"><strong>skip:</strong> {counts.skip}</span>
        <span className="mr-3 text-red-700"><strong>error:</strong> {counts.error}</span>
      </div>

      <div className="border border-gray-200 rounded bg-white max-h-[60vh] overflow-y-auto text-xs font-mono">
        {log.length === 0 ? (
          <div className="p-4 text-gray-400">No log yet — click the button above to start.</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="text-left px-3 py-2 w-20">level</th>
                <th className="text-left px-3 py-2 w-48">quote</th>
                <th className="text-left px-3 py-2">message</th>
              </tr>
            </thead>
            <tbody>
              {log.map((e, i) => (
                <tr key={i} className={
                  e.level === 'error' ? 'bg-red-50' :
                  e.level === 'ok' ? 'bg-green-50' :
                  e.level === 'dry' ? 'bg-blue-50' :
                  e.level === 'skip' ? 'bg-yellow-50' : ''
                }>
                  <td className="px-3 py-1 align-top">{e.level}</td>
                  <td className="px-3 py-1 align-top">{e.quote || '-'}</td>
                  <td className="px-3 py-1 align-top">{e.msg}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default BackfillGrandTotals;
